import React, { useEffect, useState, useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { Site, SchoolLookUpSiteData } from 'types'
import { USE_SITE_MERGE_TOOL_LINK } from './constants'
import { updateSite, siteOrgNetSuiteCustomerSearch } from 'store/sitesManager'
import EditSiteModal from './component'
import { formatSiteForSchoolLookUp } from 'lib/editNetSuite'
import {
  transformNetSuiteCustomerForForm,
  searchNSCustomersDebounced,
} from 'lib/netsuiteUtils'

interface EditSiteModalContainerProps {
  isOpen: boolean
  site?: Site
  toggle: () => void
}

interface FormValues extends SchoolLookUpSiteData {
  organizationId: number // ✅ Actually used: read in handleSubmit
  // Note: Other fields from SchoolLookUpSiteData exist in form for validation
  // but are read from currentSchool during submit, not from values
}

const EditSiteModalContainer: React.FC<EditSiteModalContainerProps> = ({
  isOpen,
  site,
  toggle,
}) => {
  const dispatch = useDispatch()
  const [currentSchool, setCurrentSchool] = useState<
    SchoolLookUpSiteData | undefined
  >()
  const [validationCache, setValidationCache] = useState<{
    value: string
    error: string
  }>({ value: '', error: '' })

  const companyName = site?.name || ''
  const assignedTo = site?.organization?.name || ''

  useEffect(() => {
    if (isOpen && site) {
      setCurrentSchool(formatSiteForSchoolLookUp(site))
      setValidationCache({ value: '', error: '' })
    }
  }, [isOpen, site])

  const schoolSelected = useCallback(
    async (formik: any, schoolData: SchoolLookUpSiteData) => {
      const {
        city,
        companyName,
        country,
        netsuiteCustomerId,
        nsCustomerId,
        organizationName,
        region,
      } = schoolData

      await Promise.all([
        formik.setFieldValue('city', city),
        formik.setFieldValue('companyName', companyName),
        formik.setFieldValue('country', country),
        formik.setFieldValue('netsuiteCustomerId', netsuiteCustomerId),
        formik.setFieldValue('nsCustomerId', nsCustomerId),
        formik.setFieldValue('region', region),
        formik.setFieldValue('organizationName', organizationName),
      ])

      setCurrentSchool(schoolData)
    },
    [],
  )

  const customerIdValidateAsync = useCallback(
    async (value: string, formik: any): Promise<string | undefined> => {
      if (value === validationCache.value) {
        return validationCache.error || undefined
      }
      if (!value) {
        return undefined
      }

      if (!/^[0-9]*$/.test(value)) {
        const error = 'Customer Id must contain only numbers'
        setValidationCache({ value, error })
        return error
      }

      if (!site) {
        return 'Site is required'
      }

      let errorMessage: string | undefined

      const duplicateCheck = siteOrgNetSuiteCustomerSearch({
        nsCustomerId: value,
        orgId: site.organization.id,
      })
        .then((res: any) => {
          const duplicates = res.data.filter(
            (s: Site) =>
              s.id !== site.id && s.organization?.id === site.organization.id,
          )
          if (duplicates.length > 0) {
            errorMessage = `Customer Id ${value} ${USE_SITE_MERGE_TOOL_LINK}`
          }
        })
        .catch(err => {
          errorMessage = err.message
        })

      const validate = searchNSCustomersDebounced(value)
        .then(response => {
          const school = response.data?.[0]
          if (school) {
            const formattedSchool = transformNetSuiteCustomerForForm(school)
            schoolSelected(formik, formattedSchool)
            errorMessage = undefined
          } else {
            errorMessage = `Cannot find Customer Id ${value}`
          }
        })
        .catch(err => {
          errorMessage = err.message || 'Error validating customer ID'
        })

      await Promise.allSettled([duplicateCheck, validate])
      setValidationCache({ value, error: errorMessage || '' })
      return errorMessage
    },
    [validationCache, site, schoolSelected],
  )

  const handleSubmit = useCallback(
    async (
      values: FormValues,
      { setSubmitting }: { setSubmitting: (isSubmitting: boolean) => void },
    ) => {
      if (!site || !currentSchool) {
        setSubmitting(false)
        return
      }

      await dispatch(
        updateSite({
          address: {
            city: currentSchool.city,
            country: currentSchool.country,
            region: currentSchool.region,
          },
          id: site.id,
          isRostered: site.isRostered,
          organizationId: values.organizationId,
          name: currentSchool.companyName || currentSchool.organizationName,
          netsuiteCustomerId: currentSchool.netsuiteCustomerId || 0,
        }),
      )
      toggle()
      setSubmitting(false)
    },
    [site, currentSchool, dispatch, toggle],
  )

  if (!site || !currentSchool) {
    return null
  }

  return (
    <EditSiteModal
      assignedTo={assignedTo}
      companyName={companyName}
      currentSchool={currentSchool}
      customerIdValidateAsync={customerIdValidateAsync}
      handleSubmit={handleSubmit}
      isOpen={isOpen}
      schoolSelected={schoolSelected}
      site={site}
      toggle={toggle}
    />
  )
}

export default EditSiteModalContainer
